<template>
    <div class="block-marketplace">
        <div class="filters-section">
            <a-input-search
                v-model="searchText"
                :placeholder="$t('chatbotv2.searchBlocks') || 'Search blocks...'"
                style="width: 300px"
                @search="handleSearch"
            />

            <a-select
                v-model="selectedCategory"
                style="width: 200px"
                :placeholder="$t('chatbotv2.category') || 'Category'"
                allowClear
                @change="handleSearch"
            >
                <a-select-option v-for="category in categoryOptions" :key="category.value" :value="category.value">
                    {{ category.label }}
                </a-select-option>
            </a-select>

            <a-select
                v-model="sortBy"
                style="width: 200px"
                @change="handleSearch"
            >
                <a-select-option value="usageCount">{{ $t("chatbotv2.mostUsed") || "Most Used" }}</a-select-option>
                <a-select-option value="rating">{{ $t("chatbotv2.topRated") || "Top Rated" }}</a-select-option>
                <a-select-option value="newest">{{ $t("chatbotv2.newest") || "Newest" }}</a-select-option>
                <a-select-option value="name">{{ $t("chatbotv2.name") || "Name" }}</a-select-option>
            </a-select>
        </div>

        <div class="blocks-grid" v-if="!loading">
            <div
                class="block-card"
                v-for="block in blocks"
                :key="block._id"
                @click="viewBlockDetails(block._id)"
            >
                <div class="block-header">
                    <span class="material-icons block-icon" :style="{ color: getBlockColor(block) }">
                        {{ block.icon || "extension" }}
                    </span>
                    <div class="block-title-section">
                        <h3>{{ block.name }}</h3>
                        <div class="block-meta">
                            <span class="category-tag">{{ block.category }}</span>
                            <span v-if="block.averageRating > 0" class="rating">
                                <a-icon type="star" theme="filled" />
                                {{ block.averageRating.toFixed(1) }}
                            </span>
                        </div>
                    </div>
                </div>

                <div class="block-description">
                    {{ block.description || $t("chatbotv2.noDescription") }}
                </div>

                <div class="block-footer">
                    <div class="usage-count">
                        <a-icon type="download" />
                        {{ block.usageCount || 0 }} {{ $t("chatbotv2.uses") }}
                    </div>
                    <a-button
                        v-if="!isImported(block._id)"
                        type="primary"
                        size="small"
                        @click.stop="importBlock(block._id)"
                    >
                        {{ $t("chatbotv2.import") }}
                    </a-button>
                    <a-tag v-else color="green">
                        {{ $t("chatbotv2.imported") }}
                    </a-tag>
                </div>
            </div>
        </div>

        <div class="loading-state" v-else>
            <a-spin size="large" />
        </div>

        <div class="empty-state" v-if="!loading && blocks.length === 0">
            <a-empty :description="$t('chatbotv2.noBlocksFound')" />
        </div>
    </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from "vuex";

export default {
    name: "BlockMarketplace",
    data() {
        return {
            searchText: "",
            selectedCategory: undefined,
            sortBy: "usageCount",
            loading: false,
            categoryOptions: [
                { value: "integration", label: "Integration", icon: "api" },
                { value: "crm", label: "CRM", icon: "contacts" },
                { value: "salesforce", label: "Salesforce", icon: "cloud" },
                { value: "zoho", label: "Zoho", icon: "cloud-server" },
                { value: "hubspot", label: "HubSpot", icon: "inbox" },
                { value: "utility", label: "Utility", icon: "tool" },
                { value: "data-transform", label: "Data Transform", icon: "swap" },
                { value: "date-functions", label: "Date Functions", icon: "calendar" },
                { value: "string-functions", label: "String Functions", icon: "font-size" },
                { value: "array-functions", label: "Array Functions", icon: "unordered-list" },
                { value: "math-functions", label: "Math Functions", icon: "calculator" },
                { value: "validation", label: "Validation", icon: "check-circle" },
                { value: "formatting", label: "Formatting", icon: "edit" },
                { value: "notification", label: "Notification", icon: "notification" },
                { value: "email", label: "Email", icon: "mail" },
                { value: "sms", label: "SMS", icon: "message" },
                { value: "payment", label: "Payment", icon: "dollar" },
                { value: "database", label: "Database", icon: "database" },
                { value: "file-operations", label: "File Operations", icon: "file" },
                { value: "custom", label: "Custom", icon: "code" },
            ],
        };
    },
    computed: {
        ...mapState({
            blocks: (state) => state.customBlocks.availableBlocks,
            site: (state) => state.site.site,
        }),
        ...mapGetters({
            isImported: "customBlocks/isImported",
        }),
    },
    methods: {
        ...mapActions({
            listAvailableBlocks: "customBlocks/listAvailableBlocks",
            importBlockAction: "customBlocks/importBlock",
        }),
        async handleSearch() {
            this.loading = true;
            try {
                await this.listAvailableBlocks({
                    search: this.searchText,
                    category: this.selectedCategory,
                    sortBy: this.sortBy,
                });
            } finally {
                this.loading = false;
            }
        },
        viewBlockDetails(blockId) {
            this.$emit("view-details", blockId);
        },
        async importBlock(blockId) {
            try {
                await this.importBlockAction({ id: blockId, versionId: null });
                this.$message.success(this.$t("chatbotv2.blockImported") || "Block imported successfully");
            } catch (error) {
                this.$message.error(error.message || "Failed to import block");
            }
        },
        getBlockColor(block) {
            const colors = {
                integration: "#1890ff",
                crm: "#722ed1",
                salesforce: "#00a1e0",
                zoho: "#e42527",
                hubspot: "#ff7a59",
                utility: "#52c41a",
                "data-transform": "#faad14",
                "date-functions": "#13c2c2",
                "string-functions": "#eb2f96",
                "array-functions": "#2f54eb",
                "math-functions": "#fa8c16",
                validation: "#52c41a",
                formatting: "#1890ff",
                notification: "#fa541c",
                email: "#597ef7",
                sms: "#73d13d",
                payment: "#ffc53d",
                database: "#9254de",
                "file-operations": "#40a9ff",
                custom: "#9C27B0",
            };
            return colors[block.category] || "#9C27B0";
        },
    },
};
</script>

<style lang="scss" scoped>
.block-marketplace {
    .filters-section {
        display: flex;
        gap: 16px;
        margin-bottom: 24px;
        flex-wrap: wrap;
    }

    .blocks-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
        gap: 20px;

        .block-card {
            background: #fff;
            border: 1px solid #e0e0e0;
            border-radius: 8px;
            padding: 20px;
            cursor: pointer;
            transition: all 0.3s;

            &:hover {
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                transform: translateY(-2px);
            }

            .block-header {
                display: flex;
                gap: 12px;
                margin-bottom: 12px;

                .block-icon {
                    font-size: 40px;
                    flex-shrink: 0;
                }

                .block-title-section {
                    flex: 1;

                    h3 {
                        margin: 0 0 8px 0;
                        font-size: 18px;
                        font-weight: 600;
                    }

                    .block-meta {
                        display: flex;
                        gap: 8px;
                        align-items: center;

                        .category-tag {
                            background: #f0f0f0;
                            padding: 2px 8px;
                            border-radius: 4px;
                            font-size: 12px;
                            text-transform: capitalize;
                        }

                        .rating {
                            font-size: 14px;
                            color: #faad14;
                        }
                    }
                }
            }

            .block-description {
                color: #666;
                font-size: 14px;
                margin-bottom: 16px;
                min-height: 40px;
                display: -webkit-box;
                -webkit-line-clamp: 2;
                -webkit-box-orient: vertical;
                overflow: hidden;
            }

            .block-footer {
                display: flex;
                justify-content: space-between;
                align-items: center;
                border-top: 1px solid #f0f0f0;
                padding-top: 12px;

                .usage-count {
                    font-size: 13px;
                    color: #999;
                }
            }
        }
    }

    .loading-state,
    .empty-state {
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 400px;
    }
}
</style>

